import React, { useState } from "react";
import api, { uploadUrl } from "../lib/api";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PhotoUpload({ value = [], onChange, label = "Photos", multiple = true, testid = "photo-upload" }) {
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
      } catch (e) {
        toast.error(`Upload failed: ${file.name}`);
      }
    }
    setUploading(false);
    if (uploaded.length) {
      onChange(multiple ? [...value, ...uploaded] : uploaded.slice(-1));
      toast.success(`${uploaded.length} file(s) uploaded`);
    }
  };

  const removeAt = (idx) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <label className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</label>
      <label
        htmlFor={`${testid}-input`}
        className="flex flex-col items-center justify-center border border-dashed border-white/15 hover:border-[#FF5722]/50 bg-[#0B0C10] rounded-sm p-6 cursor-pointer transition-colors"
      >
        {uploading ? (
          <>
            <Loader2 className="w-6 h-6 text-[#FF5722] animate-spin mb-2" />
            <span className="text-sm text-slate-400">Uploading...</span>
          </>
        ) : (
          <>
            <Upload className="w-6 h-6 text-slate-500 mb-2" />
            <span className="text-sm text-slate-300">Tap to upload / take photo</span>
            <span className="mono text-[10px] text-slate-600 mt-1">JPG · PNG · PDF · max 10MB</span>
          </>
        )}
        <input
          id={`${testid}-input`}
          data-testid={`${testid}-input`}
          type="file"
          accept="image/*,.pdf,.heic"
          multiple={multiple}
          className="hidden"
          onChange={(e) => handleFiles(Array.from(e.target.files || []))}
        />
      </label>

      {value.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
          {value.map((url, i) => {
            const isPdf = url.toLowerCase().endsWith(".pdf");
            return (
              <div key={i} className="relative group border border-white/10 rounded-sm overflow-hidden bg-[#0B0C10] aspect-square">
                {isPdf ? (
                  <div className="flex items-center justify-center h-full mono text-xs text-slate-400">PDF</div>
                ) : (
                  <img src={uploadUrl(url)} alt="" className="w-full h-full object-cover" />
                )}
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-black/70 border border-white/20 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`${testid}-remove-${i}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
